import { getLinkForCourseLesson } from "../../../../services/Utils";

import lesson0102 from "../lesson-02/lesson";
const linkLesson0102 = getLinkForCourseLesson("41545ab2-1f99-40ea-8225-21d5e62ab7b0", lesson0102.id);

const theory = `<p>
Note: This chapter makes use of vectors noted straight bold whereas the scalars are noted in italics (as an example
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi mathvariant="bold">v</mi>
</math>
is the velocity vector and
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>v</mi>
      </mrow>
      <mrow>
         <mi>x</mi>
      </mrow>
   </msub>
</math>
its component along
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>x</mi>
</math>
). The scalar product is denoted
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mo class=MathClass-bin>⋅</mo>
</math>
.
<p>
We establish here the equations of acoustics without dissipation and external sources. These equations are used to determine the three-dimensional wave equation. The equations acoustics in three dimensions can be viewed as a generalization of equations in one dimension (see <a href="${linkLesson0102}" target="_blank">Lesson 1.2</a>), a problem in three dimensions
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mo class=MathClass-open>(</mo>
   <mi>x</mi>
   <mo class=MathClass-punc>,</mo>
   <mi>y</mi>
   <mo class=MathClass-punc>,</mo>
   <mi>z</mi>
   <mo class=MathClass-close>)</mo>
</math>
can be seen as the superposition of three one-dimensional problems, respectively along
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>x</mi>
</math>
,
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>y</mi>
</math>
and
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>z</mi>
</math>.
<h3 class=sectionHead>Equations of acoustics in three dimensions</h3>
<p>Consider a certain amount of fluid contained in a variable volume V. The fundamental relation of dynamics applied to this volume of fluid is written
<table class=equation>
<tr>
   <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
         <msub>
            <mrow>
               <mi>∯</mi>
            </mrow>
            <mrow>
               <mi>S</mi>
            </mrow>
         </msub>
         <mo class=MathClass-bin>−</mo>
         <mi mathvariant=italic>P</mi>
         <mi mathvariant=bold>n</mi>
         <mstyle mathvariant=normal>
            <mi>d</mi>
         </mstyle>
         <mi>S</mi>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mo>∭</mo>
            </mrow>
            <mrow>
               <mi>V</mi>
            </mrow>
         </msub>
         <mi>ρ</mi>
         <mfrac>
            <mrow>
               <mi mathvariant=italic>d</mi>
               <mi mathvariant=bold>v</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>dt</mi>
            </mrow>
         </mfrac>
         <mstyle mathvariant=normal>
            <mi>d</mi>
         </mstyle>
         <mi>V</mi>
      </math>
   <td class=eq-no>(1)
</table>
<p>
where
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>P</mi>
</math>
is the total pressure,
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>S</mi>
</math>
the closed surface containing the volume
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>V</mi>
</math>
and
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi mathvariant=bold>n</mi>
</math>
the vector normal to the surface
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <mi>S</mi>
</math>. It is recalled here that the force resulting from a pressure is normal to the surface. Moreover tangential forces due to viscosity are not considered here (ideal fluid assumption).
<p>
As
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>∯</mi>
      </mrow>
      <mrow>
         <mi>S</mi>
      </mrow>
   </msub>
   <mo class=MathClass-bin>−</mo>
   <mi mathvariant=italic>P</mi>
   <mi mathvariant=bold>n</mi>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <mi>S</mi>
   <mo class=MathClass-rel>=</mo>
   <msub>
      <mrow>
         <mi class=MathClass-op>∭</mi>
         <mo>⁡</mo>
      </mrow>
      <mrow>
         <mi>V</mi>
      </mrow>
   </msub>
   <mi mathvariant=bold>grad</mi>
   <mi mathvariant=italic>P</mi>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <mi>V</mi>
</math>
and Equation (1) being true whatever the considered volume we can write
<table class=equation>
<tr>
   <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
         <mi>ρ</mi>
         <mfrac>
            <mrow>
               <mstyle mathvariant=normal>
                  <mi>d</mi>
               </mstyle>
               <mi>v</mi>
            </mrow>
            <mrow>
               <mstyle mathvariant=normal>
                  <mi>d</mi>
               </mstyle>
               <mi>t</mi>
            </mrow>
         </mfrac>
         <mo class=MathClass-rel>=</mo>
         <mo class=MathClass-bin>−</mo>
         <mi mathvariant=bold>grad</mi>
         <mi mathvariant=italic>P</mi>
      </math>
   <td class=eq-no>(2)
</table>
<p>
This is the Euler equation. In the context of linear acoustics, fluctuations are assumed small compared to the average values (except for the particle velocity which is assumed small compared to the speed of sound). We then set
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>P</mi>
   <mo class=MathClass-rel>=</mo>
   <msub>
      <mrow>
         <mi>P</mi>
      </mrow>
      <mrow>
         <mi mathvariant=italic>atm</mi>
      </mrow>
   </msub>
   <mo class=MathClass-bin>+</mo>
   <mi>p</mi>
</math>
where
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>P</mi>
      </mrow>
      <mrow>
         <mi mathvariant=italic>atm</mi>
      </mrow>
   </msub>
</math>, the average pressure (atmospheric pressure in most situations), is assumed to be uniform and
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>p</mi>
</math>
is the actual sound pressure. We set
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>ρ</mi>
   <mo class=MathClass-rel>=</mo>
   <msub>
      <mrow>
         <mi>ρ</mi>
      </mrow>
      <mrow>
         <mn>0</mn>
      </mrow>
   </msub>
   <mo class=MathClass-bin>+</mo>
   <msup>
      <mrow>
         <mi>ρ</mi>
      </mrow>
      <mrow>
         <mi>′</mi>
      </mrow>
   </msup>
</math>
where the average density
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>ρ</mi>
      </mrow>
      <mrow>
         <mn>0</mn>
      </mrow>
   </msub>
</math>
is assumed to be uniform and
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msup>
      <mrow>
         <mi>ρ</mi>
      </mrow>
      <mrow>
         <mi>′</mi>
      </mrow>
   </msup>
</math>
are density fluctuations. Finally
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi mathvariant=bold>v</mi>
   <mo class=MathClass-rel>=</mo>
   <msub>
      <mrow>
         <mi mathvariant=bold>U</mi>
      </mrow>
      <mrow>
         <mn>0</mn>
      </mrow>
   </msub>
   <mo class=MathClass-bin>+</mo>
   <msub>
      <mrow>
         <mi mathvariant=bold>v</mi>
      </mrow>
      <mrow>
         <mi mathvariant=italic>ac</mi>
      </mrow>
   </msub>
</math>
where
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi mathvariant=bold>U</mi>
      </mrow>
      <mrow>
         <mn>0</mn>
      </mrow>
   </msub>
</math>
is the mean velocity and
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>v</mi>
      </mrow>
      <mrow>
         <mi mathvariant=italic>ac</mi>
      </mrow>
   </msub>
</math>
the acoustic velocity. Euler equation considering
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>P</mi>
      </mrow>
      <mrow>
         <mi mathvariant=italic>atm</mi>
      </mrow>
   </msub>
   <mo class=MathClass-rel>></mo>
   <mo class=MathClass-rel>></mo>
   <mi>p</mi>
</math>
and
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>ρ</mi>
      </mrow>
      <mrow>
         <mn>0</mn>
      </mrow>
   </msub>
   <mo class=MathClass-rel>></mo>
   <mo class=MathClass-rel>></mo>
   <msup>
      <mrow>
         <mi>ρ</mi>
      </mrow>
      <mrow>
         <mi>′</mi>
      </mrow>
   </msup>
</math>
becomes
<table class=equation>
<tr>
   <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
         <msub>
            <mrow>
               <mi>ρ</mi>
            </mrow>
            <mrow>
               <mn>0</mn>
            </mrow>
         </msub>
         <mfrac>
            <mrow>
               <mstyle mathvariant=normal>
                  <mi>d</mi>
               </mstyle>
               <mi>v</mi>
            </mrow>
            <mrow>
               <mstyle mathvariant=normal>
                  <mi>d</mi>
               </mstyle>
               <mi>t</mi>
            </mrow>
         </mfrac>
         <mo class=MathClass-rel>=</mo>
         <mo class=MathClass-bin>−</mo>
         <mi mathvariant=bold>grad</mi>
         <mi mathvariant=italic>p</mi>
      </math>
   <td class=eq-no>(3)
</table>
<p>or
<table class=equation>
<tr>
   <td style="font-size: 15px">
      <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
         <msub>
            <mrow>
               <mi>ρ</mi>
            </mrow>
            <mrow>
               <mn>0</mn>
            </mrow>
         </msub>
         <mrow>
            <mo form=prefix fence=true>(</mo>
            <mrow>
               <mfrac>
                  <mrow>
                     <mi>∂</mi>
                     <msub>
                        <mrow>
                           <mi mathvariant=bold>v</mi>
                        </mrow>
                        <mrow>
                           <mi mathvariant=italic>ac</mi>
                        </mrow>
                     </msub>
                  </mrow>
                  <mrow>
                     <mi mathvariant=italic>∂t</mi>
                  </mrow>
               </mfrac>
               <mo class=MathClass-bin>+</mo>
               <msub>
                  <mrow>
                     <mi mathvariant=bold>U</mi>
                  </mrow>
                  <mrow>
                     <mn>0</mn>
                  </mrow>
               </msub>
               <mo class=MathClass-bin>⋅</mo>
               <mi mathvariant=bold>grad</mi>
               <msub>
                  <mrow>
                     <mi mathvariant=bold>v</mi>
                  </mrow>
                  <mrow>
                     <mi mathvariant=italic>ac</mi>
                  </mrow>
               </msub>
            </mrow>
            <mo form=postfix fence=true>)</mo>
         </mrow>
         <mo class=MathClass-rel>=</mo>
         <mo class=MathClass-bin>−</mo>
         <mi mathvariant=bold>grad</mi>
         <mi mathvariant=italic>p</mi>
      </math>
   <td class=eq-no>(4)
</table>
<p>
In this last expression
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi mathvariant=bold>grad</mi>
   <msub>
      <mrow>
         <mi mathvariant=bold>v</mi>
      </mrow>
      <mrow>
         <mi mathvariant=italic>ac</mi>
      </mrow>
   </msub>
</math>
is the gradient of a vector and therefore a tensor of order 2. In the absence of flow
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi mathvariant=bold>U</mi>
      </mrow>
      <mrow>
         <mn>0</mn>
      </mrow>
   </msub>
</math>
the linearized Euler equation reduces to
<table class=equation>
<tr>
   <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
         <msub>
            <mrow>
               <mi>ρ</mi>
            </mrow>
            <mrow>
               <mn>0</mn>
            </mrow>
         </msub>
         <mfrac>
            <mrow>
               <mi>∂</mi>
               <msub>
                  <mrow>
                     <mi mathvariant=bold>v</mi>
                  </mrow>
                  <mrow>
                     <mi mathvariant=italic>ac</mi>
                  </mrow>
               </msub>
            </mrow>
            <mrow>
               <mi mathvariant=italic>∂t</mi>
            </mrow>
         </mfrac>
         <mo class=MathClass-rel>=</mo>
         <mo class=MathClass-bin>−</mo>
         <mi mathvariant=bold>grad</mi>
         <mi mathvariant=italic>p</mi>
      </math>
   <td class=eq-no>(5)
</table>
<h3 class=sectionHead>Mass conservation equation</h3>
<p>
Now consider a fixed volume
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>V</mi>
</math>. A mass balance is written on the volume during a time
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <mi>t</mi>
</math>
<table class=equation>
<tr>
   <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
         <mstyle mathvariant=normal>
            <mi>d</mi>
         </mstyle>
         <msub>
            <mrow>
               <mo>∭</mo>
            </mrow>
            <mrow>
               <mi>V</mi>
            </mrow>
         </msub>
         <mi>ρ</mi>
         <mstyle mathvariant=normal>
            <mi>d</mi>
         </mstyle>
         <mi>V</mi>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mi>∯</mi>
            </mrow>
            <mrow>
               <mi>S</mi>
            </mrow>
         </msub>
         <mi>ρ</mi>
         <mo class=MathClass-open>(</mo>
         <mi mathvariant=bold>v</mi>
         <mstyle mathvariant=normal>
            <mi>d</mi>
         </mstyle>
         <mi>t</mi>
         <mo class=MathClass-bin>⋅</mo>
         <mstyle mathvariant=normal>
            <mi>d</mi>
         </mstyle>
         <mi mathvariant=bold>S</mi>
         <mo class=MathClass-close>)</mo>
      </math>
   <td class=eq-no>(6)
</table>
<p>
Left term is the change in mass of the volume, whereas the right-hand term represents the mass that comes out of the same volume. Knowing that
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>∯</mi>
      </mrow>
      <mrow>
         <mi>S</mi>
      </mrow>
   </msub>
   <mi>ρ</mi>
   <mo class=MathClass-open>(</mo>
   <mi mathvariant=bold>v</mi>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <mi>t</mi>
   <mo class=MathClass-bin>⋅</mo>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <mi mathvariant=bold>S</mi>
   <mo class=MathClass-close>)</mo>
   <mo class=MathClass-rel>=</mo>
   <msub>
      <mrow>
         <mi class=MathClass-op>∭</mi>
         <mo>⁡</mo>
      </mrow>
      <mrow>
         <mi>V</mi>
      </mrow>
   </msub>
   <mstyle class=text>
      <mtext>div</mtext>
   </mstyle>
   <mo class=MathClass-open>(</mo>
   <mi mathvariant=italic>ρ</mi>
   <mi mathvariant=bold>v</mi>
   <mo class=MathClass-close>)</mo>
   <mstyle mathvariant=normal>
      <mi>d</mi>
   </mstyle>
   <mi>V</mi>
</math>
and that Equation (6) is valid whatever the considered volume, we can write
<table class=equation>
<tr>
   <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
         <mstyle class=text>
            <mtext>div</mtext>
         </mstyle>
         <mo class=MathClass-open>(</mo>
         <mi mathvariant=italic>ρ</mi>
         <mi mathvariant=bold>v</mi>
         <mo class=MathClass-close>)</mo>
         <mo class=MathClass-rel>=</mo>
         <mo class=MathClass-bin>−</mo>
         <mfrac>
            <mrow>
               <mi mathvariant=italic>∂ρ</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>∂t</mi>
            </mrow>
         </mfrac>
      </math>
   <td class=eq-no>(7)
</table>
<p>
Note that because
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mstyle class=text>
      <mtext>div</mtext>
   </mstyle>
   <mo class=MathClass-open>(</mo>
   <mi mathvariant=italic>ρ</mi>
   <mi mathvariant=bold>v</mi>
   <mo class=MathClass-close>)</mo>
   <mo class=MathClass-rel>=</mo>
   <mi>ρ</mi>
   <mstyle class=text>
      <mtext>div</mtext>
   </mstyle>
   <mo class=MathClass-open>(</mo>
   <mi mathvariant=bold>v</mi>
   <mo class=MathClass-close>)</mo>
   <mo class=MathClass-bin>+</mo>
   <mi mathvariant=bold>v</mi>
   <mo class=MathClass-bin>⋅</mo>
   <mi mathvariant=bold>grad</mi>
   <mi mathvariant=italic>ρ</mi>
</math>
and
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mfrac>
      <mrow>
         <mstyle mathvariant=normal>
            <mi>d</mi>
         </mstyle>
         <mi>ρ</mi>
      </mrow>
      <mrow>
         <mstyle mathvariant=normal>
            <mi>d</mi>
         </mstyle>
         <mi>t</mi>
      </mrow>
   </mfrac>
   <mo class=MathClass-rel>=</mo>
   <mfrac>
      <mrow>
         <mi mathvariant=italic>∂ρ</mi>
      </mrow>
      <mrow>
         <mi mathvariant=italic>∂t</mi>
      </mrow>
   </mfrac>
   <mo class=MathClass-bin>+</mo>
   <mi mathvariant=bold>v</mi>
   <mo class=MathClass-bin>⋅</mo>
   <mi mathvariant=bold>grad</mi>
   <mi mathvariant=italic>ρ</mi>
</math>
, we may as well write
<table class=equation>
<tr>
   <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
         <mi>ρ</mi>
         <mstyle class=text>
            <mtext>div</mtext>
         </mstyle>
         <mo class=MathClass-open>(</mo>
         <mi mathvariant=bold>v</mi>
         <mo class=MathClass-close>)</mo>
         <mo class=MathClass-rel>=</mo>
         <mo class=MathClass-bin>−</mo>
         <mfrac>
            <mrow>
               <mstyle mathvariant=normal>
                  <mi>d</mi>
               </mstyle>
               <mi>ρ</mi>
            </mrow>
            <mrow>
               <mstyle mathvariant=normal>
                  <mi>d</mi>
               </mstyle>
               <mi>t</mi>
            </mrow>
         </mfrac>
      </math>
   <td class=eq-no>(8)
</table>
<p>In the context of linear acoustics, fluctuations are assumed small compared to the mean values and the mass conservation equation becomes
<table class=equation>
<tr>
   <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
         <msub>
            <mrow>
               <mi>ρ</mi>
            </mrow>
            <mrow>
               <mn>0</mn>
            </mrow>
         </msub>
         <mstyle class=text>
            <mtext>div</mtext>
         </mstyle>
         <mo class=MathClass-open>(</mo>
         <msub>
            <mrow>
               <mi mathvariant=bold>v</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>ac</mi>
            </mrow>
         </msub>
         <mo class=MathClass-close>)</mo>
         <mo class=MathClass-rel>=</mo>
         <mo class=MathClass-bin>−</mo>
         <mfrac>
            <mrow>
               <mi>∂</mi>
               <msup>
                  <mrow>
                     <mi>ρ</mi>
                  </mrow>
                  <mrow>
                     <mi>′</mi>
                  </mrow>
               </msup>
            </mrow>
            <mrow>
               <mi mathvariant=italic>∂t</mi>
            </mrow>
         </mfrac>
      </math>
   <td class=eq-no>(9)
</table>
<h3 class=sectionHead>State equation</h3>
<p>This equation being scalar, it is the same in 1D as in 3D (see <a href="${linkLesson0102}" target="_blank">Lesson 1.2</a>). It leads to
<table class=equation>
<tr>
   <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
         <mi>p</mi>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <mi>γ</mi>
               <msub>
                  <mrow>
                     <mi>P</mi>
                  </mrow>
                  <mrow>
                     <mi mathvariant=italic>atm</mi>
                  </mrow>
               </msub>
            </mrow>
            <mrow>
               <mi>ρ</mi>
            </mrow>
         </mfrac>
         <msup>
            <mrow>
               <mi>ρ</mi>
            </mrow>
            <mrow>
               <mi>′</mi>
            </mrow>
         </msup>
         <mo class=MathClass-rel>=</mo>
         <msup>
            <mrow>
               <mi>ρ</mi>
            </mrow>
            <mrow>
               <mi>′</mi>
            </mrow>
         </msup>
         <msup>
            <mrow>
               <mi>c</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msup>
      </math>
   <td class=eq-no>(10)
</table>
<p>
where
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>c</mi>
   <mo class=MathClass-rel>=</mo>
   <msqrt>
      <mrow>
         <mfrac>
            <mrow>
               <mi>γ</mi>
               <msub>
                  <mrow>
                     <mi>P</mi>
                  </mrow>
                  <mrow>
                     <mi mathvariant=italic>atm</mi>
                  </mrow>
               </msub>
            </mrow>
            <mrow>
               <mi>ρ</mi>
            </mrow>
         </mfrac>
      </mrow>
   </msqrt>
   <mo class=MathClass-rel>=</mo>
   <msqrt>
      <mrow>
         <mfrac>
            <mrow>
               <mi mathvariant=italic>γRT</mi>
            </mrow>
            <mrow>
               <mi>M</mi>
            </mrow>
         </mfrac>
      </mrow>
   </msqrt>
</math>
is the speed of sound.
<h3 class=sectionHead>The three dimensional wave equation</h3>
<p>
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msup>
      <mrow>
         <mi>ρ</mi>
      </mrow>
      <mrow>
         <mi>′</mi>
      </mrow>
   </msup>
</math>
can be replaced by its expression as a function of the acoustic pressure in the mass conservation equation. This leads to
<table class=equation>
<tr>
   <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
      <mo form=prefix fence=true>{</mo>
        <mfrac linethickness=0>
         <mrow>
         <mi mathvariant=bold>grad</mi>
         <mi mathvariant=italic>p</mi>
         <mo class=MathClass-rel>=</mo>
         <mo class=MathClass-bin>−</mo>
         <msub>
            <mrow>
               <mi>ρ</mi>
            </mrow>
            <mrow>
               <mn>0</mn>
            </mrow>
         </msub>
         <mfrac>
            <mrow>
               <mi mathvariant=italic>∂v</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>∂t</mi>
            </mrow>
         </mfrac>
         </mrow>
         <mrow>
            <mrow>
               <mstyle class=text>
                  <mtext>div</mtext>
               </mstyle>
               <mi mathvariant=bold>v</mi>
               <mo class=MathClass-rel>=</mo>
               <mo class=MathClass-bin>−</mo>
               <mfrac>
                  <mrow>
                     <mn>1</mn>
                  </mrow>
                  <mrow>
                     <msub>
                        <mrow>
                           <mi>ρ</mi>
                        </mrow>
                        <mrow>
                           <mn>0</mn>
                        </mrow>
                     </msub>
                     <msup>
                        <mrow>
                           <mi>c</mi>
                        </mrow>
                        <mrow>
                           <mn>2</mn>
                        </mrow>
                     </msup>
                  </mrow>
               </mfrac>
               <mfrac>
                  <mrow>
                     <mi mathvariant=italic>∂p</mi>
                  </mrow>
                  <mrow>
                     <mi mathvariant=italic>∂t</mi>
                  </mrow>
               </mfrac>
            </mrow>
         </mrow>
        </mfrac>
      </math>
   <td class=eq-no>(11)
</table>
<p>
For deriving the wave equation in three dimensions, it is practiced in the same way as for the 1-D problem (see <a href="${linkLesson0102}" target="_blank">Lesson 1.2</a>) that is to say that one of the equation is multiplied by
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mfrac>
      <mrow>
         <mi>∂</mi>
      </mrow>
      <mrow>
         <mi mathvariant=italic>∂t</mi>
      </mrow>
   </mfrac>
</math>
and the other by
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mstyle class=text>
      <mtext>div</mtext>
   </mstyle>
</math>. So, we obtain
<table class=equation>
<tr>
   <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
      <mo form=prefix fence=true>{</mo>
        <mfrac linethickness=0>
         <mrow>
               <mstyle class=text>
                  <mtext>div</mtext>
               </mstyle>
               <mo class=MathClass-open>(</mo>
               <mi mathvariant=bold>grad</mi>
               <mi mathvariant=italic>p</mi>
               <mo class=MathClass-close>)</mo>
               <mo class=MathClass-rel>=</mo>
               <mo class=MathClass-bin>−</mo>
               <msub>
                  <mrow>
                     <mi>ρ</mi>
                  </mrow>
                  <mrow>
                     <mn>0</mn>
                  </mrow>
               </msub>
               <mfrac>
                  <mrow>
                     <mi>∂</mi>
                     <mstyle class=text>
                        <mtext>div</mtext>
                     </mstyle>
                     <mi mathvariant=bold>v</mi>
                  </mrow>
                  <mrow>
                     <mi mathvariant=italic>∂t</mi>
                  </mrow>
               </mfrac>
         </mrow>
         <mrow>
               <mfrac>
                  <mrow>
                     <mi>∂</mi>
                     <mstyle class=text>
                        <mtext>div</mtext>
                     </mstyle>
                     <mi mathvariant=bold>v</mi>
                  </mrow>
                  <mrow>
                     <mi mathvariant=italic>∂t</mi>
                  </mrow>
               </mfrac>
               <mo class=MathClass-rel>=</mo>
               <mo class=MathClass-bin>−</mo>
               <mfrac>
                  <mrow>
                     <mn>1</mn>
                  </mrow>
                  <mrow>
                     <msub>
                        <mrow>
                           <mi>ρ</mi>
                        </mrow>
                        <mrow>
                           <mn>0</mn>
                        </mrow>
                     </msub>
                     <msup>
                        <mrow>
                           <mi>c</mi>
                        </mrow>
                        <mrow>
                           <mn>2</mn>
                        </mrow>
                     </msup>
                  </mrow>
               </mfrac>
               <mfrac>
                  <mrow>
                     <msup>
                        <mrow>
                           <mi>∂</mi>
                        </mrow>
                        <mrow>
                           <mn>2</mn>
                        </mrow>
                     </msup>
                     <mi>p</mi>
                  </mrow>
                  <mrow>
                     <mi>∂</mi>
                     <msup>
                        <mrow>
                           <mi>t</mi>
                        </mrow>
                        <mrow>
                           <mn>2</mn>
                        </mrow>
                     </msup>
                  </mrow>
               </mfrac>
            </mrow>
         </mrow>
         </mfrac>
      </math>
   <td class=eq-no>(12)
</table>
<p>
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mstyle class=text>
      <mtext>div</mtext>
   </mstyle>
   <mo class=MathClass-open>(</mo>
   <mi mathvariant=bold>grad</mi>
   <mo class=MathClass-close>)</mo>
   <mo class=MathClass-rel>=</mo>
   <mfrac>
      <mrow>
         <msup>
            <mrow>
               <mi>∂</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msup>
      </mrow>
      <mrow>
         <mi>∂</mi>
         <msup>
            <mrow>
               <mi>x</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msup>
      </mrow>
   </mfrac>
   <mo class=MathClass-bin>+</mo>
   <mfrac>
      <mrow>
         <msup>
            <mrow>
               <mi>∂</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msup>
      </mrow>
      <mrow>
         <mi>∂</mi>
         <msup>
            <mrow>
               <mi>y</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msup>
      </mrow>
   </mfrac>
   <mo class=MathClass-bin>+</mo>
   <mfrac>
      <mrow>
         <msup>
            <mrow>
               <mi>∂</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msup>
      </mrow>
      <mrow>
         <mi>∂</mi>
         <msup>
            <mrow>
               <mi>z</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msup>
      </mrow>
   </mfrac>
</math>
is the laplacian
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>Δ</mi>
</math>. The 3-D wave equation is then
<table class=equation>
<tr>
   <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
         <mi mathvariant=italic>Δp</mi>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <mn>1</mn>
            </mrow>
            <mrow>
               <msup>
                  <mrow>
                     <mi>c</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msup>
            </mrow>
         </mfrac>
         <mfrac>
            <mrow>
               <msup>
                  <mrow>
                     <mi>∂</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msup>
               <mi>p</mi>
            </mrow>
            <mrow>
               <mi>∂</mi>
               <msup>
                  <mrow>
                     <mi>t</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msup>
            </mrow>
         </mfrac>
      </math>
   <td class=eq-no>(13)
</table>
<p>Note that this expression is valid regardless of the coordinate system provided the use of the adequate expression of the Laplacian. The Helmholtz equation associated (wave equation in the frequency domain) can be written
<table class=equation>
<tr>
   <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
         <mi mathvariant=italic>Δp</mi>
         <mo class=MathClass-bin>+</mo>
         <msup>
            <mrow>
               <mi>k</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msup>
         <mi>p</mi>
         <mo class=MathClass-rel>=</mo>
         <mn>0</mn>
      </math>
   <td class=eq-no>(14)
</table>
<p>
with
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>k</mi>
   <mo class=MathClass-rel>=</mo>
   <mi>ω</mi>
   <mo class=MathClass-bin>∕</mo>
   <mi>c</mi>
</math>
the wave number. The same wave equation is valid for density but not for the velocity. Since the velocity is a vector, the Laplacian is a vector Laplacian that is to say a vector whose components are
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>Δ</mi>
   <msub>
      <mrow>
         <mi>v</mi>
      </mrow>
      <mrow>
         <mi>x</mi>
      </mrow>
   </msub>
</math>
,
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>Δ</mi>
   <msub>
      <mrow>
         <mi>v</mi>
      </mrow>
      <mrow>
         <mi>y</mi>
      </mrow>
   </msub>
</math>
and
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>Δ</mi>
   <msub>
      <mrow>
         <mi>v</mi>
      </mrow>
      <mrow>
         <mi>z</mi>
      </mrow>
   </msub>
</math>. One obtains
<table class=equation>
<tr>
   <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
         <mi mathvariant=italic>Δ</mi>
         <mi mathvariant=bold>v</mi>
         <mo class=MathClass-bin>+</mo>
         <mi mathvariant=bold>rot</mi>
         <mo class=MathClass-open stretchy=false>(</mo>
         <mi mathvariant=bold>rot</mi>
         <mo class=MathClass-open stretchy=false>(</mo>
         <mi mathvariant=bold>v</mi>
         <mo class=MathClass-close stretchy=false>)</mo>
         <mo class=MathClass-close stretchy=false>)</mo>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <mn>1</mn>
            </mrow>
            <mrow>
               <msup>
                  <mrow>
                     <mi>c</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msup>
            </mrow>
         </mfrac>
         <mfrac>
            <mrow>
               <msup>
                  <mrow>
                     <mi>∂</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msup>
               <mi mathvariant=bold>v</mi>
            </mrow>
            <mrow>
               <mi>∂</mi>
               <msup>
                  <mrow>
                     <mi>t</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msup>
            </mrow>
         </mfrac>
      </math>
   <td class=eq-no>(15)
</table>
<p>
In acoustics the vorticity
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi mathvariant=bold>rot</mi>
    <mi mathvariant=bold>v</mi>
</math>
is null and one has simply:
<table class=equation>
<tr>
   <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
         <mi mathvariant=italic>Δ</mi>
         <mi mathvariant=bold>v</mi>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <mn>1</mn>
            </mrow>
            <mrow>
               <msup>
                  <mrow>
                     <mi>c</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msup>
            </mrow>
         </mfrac>
         <mfrac>
            <mrow>
               <msup>
                  <mrow>
                     <mi>∂</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msup>
               <mi mathvariant=bold>v</mi>
            </mrow>
            <mrow>
               <mi>∂</mi>
               <msup>
                  <mrow>
                     <mi>t</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msup>
            </mrow>
         </mfrac>
      </math>
   <td class=eq-no>(16)
</table>
<h3 class=sectionHead>Velocity potential</h3>
<p>
It can be shown that any vector field can be decomposed into an irrotational field and an indivergent field. Aware that
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi mathvariant=bold>rot</mi>
   <mo class=MathClass-open>(</mo>
   <mi mathvariant=bold>grad</mi>
   <mo class=MathClass-close>)</mo>
   <mo class=MathClass-rel>=</mo>
   <mn>0</mn>
</math>
and
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mstyle class=text>
      <mtext>div</mtext>
   </mstyle>
   <mo class=MathClass-open>(</mo>
   <mi mathvariant=bold>rot</mi>
   <mo class=MathClass-close>)</mo>
   <mo class=MathClass-rel>=</mo>
   <mn>0</mn>
</math>, it can be written
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi mathvariant=bold>v</mi>
   <mo class=MathClass-rel>=</mo>
   <mo class=MathClass-bin>−</mo>
   <mi mathvariant=bold>grad</mi>
   <mi mathvariant=italic>ϕ</mi>
   <mo class=MathClass-bin>+</mo>
   <mi mathvariant=bold>rot</mi>
   <mi mathvariant=italic>ψ</mi>
</math>.
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>ϕ</mi>
</math>
is the velocity potential (irrotational) and
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>ψ</mi>
</math>
is the potential of vortices (indivergent). It is easy to check that the velocity potential is solution of the wave equation:
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi mathvariant=italic>Δϕ</mi>
   <mo class=MathClass-rel>=</mo>
   <mfrac>
      <mrow>
         <mn>1</mn>
      </mrow>
      <mrow>
         <msup>
            <mrow>
               <mi>c</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msup>
      </mrow>
   </mfrac>
   <mfrac>
      <mrow>
         <msup>
            <mrow>
               <mi>∂</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msup>
         <mi>ϕ</mi>
      </mrow>
      <mrow>
         <mi>∂</mi>
         <msup>
            <mrow>
               <mi>t</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msup>
      </mrow>
   </mfrac>
</math>. Some authors prefer to solve the wave equation for the velocity potential and deduce the pressure by the relation
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>p</mi>
   <mo class=MathClass-rel>=</mo>
   <msub>
      <mrow>
         <mi>ρ</mi>
      </mrow>
      <mrow>
         <mn>0</mn>
      </mrow>
   </msub>
   <mfrac>
      <mrow>
         <mi mathvariant=italic>∂ϕ</mi>
      </mrow>
      <mrow>
         <mi mathvariant=italic>∂t</mi>
      </mrow>
   </mfrac>
</math>
which is another form of the Euler equation and the velocity by the relation
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>v</mi>
   <mo class=MathClass-rel>=</mo>
   <mo class=MathClass-bin>−</mo>
   <mi mathvariant=bold>grad</mi>
   <mi mathvariant=italic>ϕ</mi>
</math>.
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt>[1]
   <dd>L.J. Beranek. Acoustics. The American Institute of Physics, New York, 1986.
   <dt>[2]
   <dd>M. Bruneau. Manuel d’Acoustique Fondamentale. Hermes, Paris, 1998.
   <dt>[3]
   <dd>A. Chaigne and J. Kergomard. Acoustics of Musical Instruments. Springer, New York, 2016.
   <dt>[4]
   <dd>A.D. Pierce. Acoustics: an Introduction to its Physical Principles and Applications. MCGraw-Hill Book Company, Inc., New York, 1981.
   <dt>[5]
   <dd>E. Skudrzyk. The Foundations of Acoustics—Basic Mathematics and Basic Acoustics. Springer–Verlag, 1971.
   <dt>[6]
   <dd>S. Temkin. Elements of Acoustics. Wiley, New York, 1981.
</dl>`;

export default theory;
